import Touchable from '@components/Touchable'
import Stealthex, { ExchangeApiResponse } from '@plug-core/clients/Stealthex'
import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { AnimationPlayer } from '@components/AnimationPlayer'
import loadingAnimation from '@assets/lottie/loading.json'
import { Box, Chip, Container, Link, Stack, Typography } from '@mui/material'
import useToast from '@hooks/useToast'
import { logger } from '@plug-core/logger'

const client = new Stealthex()
const REFETCH_INTERVAL = 5000

const SUPPORT_URL = 'https://stealthex.freshdesk.com/support/tickets/new'

const ConvertResult = () => {
  const [exchangeStatus, setExchangeStatus] = useState<ExchangeApiResponse>()

  const { t } = useTranslation()
  const { setToastMessage } = useToast()
  const {
    state: { id },
  } = useLocation()

  const fetchExchangeStatus = useCallback(() => {
    client.getExchangeInfo(id).then(setExchangeStatus).catch(logger.debug)
  }, [id])

  useEffect(() => {
    fetchExchangeStatus()

    const interval = setInterval(() => {
      fetchExchangeStatus()
    }, REFETCH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [fetchExchangeStatus])

  const renderRow = (label: string, value?: string, action?: () => void, alert?: string) => (
    <Box sx={{ wordBreak: 'break-word' }}>
      <Typography fontWeight="lighter" color="gray">
        {label}
      </Typography>
      {!!alert && (
        <Container>
          <Typography>{alert}</Typography>
        </Container>
      )}
      <Touchable disabled={!action} onClick={action}>
        <Typography>{value}</Typography>
      </Touchable>
    </Box>
  )

  const copyValue = (value: string, notification: string) => {
    setToastMessage(notification)
    navigator.clipboard.writeText(value)
  }

  const renderAddress = () => (
    <>
      {renderRow(
        t('convert.youSend'),
        `${exchangeStatus?.data?.amount_from} ${exchangeStatus?.data?.currency_from?.toUpperCase()}`
      )}
      {renderRow(t('convert.toAddress'), exchangeStatus?.data?.address_from, () =>
        copyValue(exchangeStatus?.data?.address_from || '', 'Deposit Address Copied')
      )}
    </>
  )

  if (!exchangeStatus)
    return (
      <Stack justifyContent="center" style={{ flex: 1 }}>
        <Box style={{ height: 300, width: 300, alignSelf: 'center' }}>
          <AnimationPlayer animationData={loadingAnimation} />
        </Box>
      </Stack>
    )

  return (
    <Stack spacing={2} px={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography fontWeight="lighter" color="gray">
          {t('convert.exchangeId')}
        </Typography>
        <Touchable onClick={() => copyValue(exchangeStatus?.data?.id, 'ID copied')}>
          <Chip color="primary" size="small" label={exchangeStatus?.data?.id} />
        </Touchable>
      </Stack>
      <Stack direction="row" alignItems="center">
        <Typography variant="h5">
          {t(`convert.statuses.${exchangeStatus?.data?.status}`)}
        </Typography>
        <AnimationPlayer animationData={loadingAnimation} />
      </Stack>

      {exchangeStatus?.data?.currency_from !== 'icp' && renderAddress()}
      {renderRow(
        t('convert.youReceive'),
        `≈ ${exchangeStatus?.data?.amount_to} ${exchangeStatus?.data?.currency_to?.toUpperCase()}`
      )}
      {renderRow(t('convert.recepientsAddress'), exchangeStatus?.data?.address_to)}

      {exchangeStatus?.data?.extra_id_from &&
        renderRow(
          exchangeStatus.data.currencies[exchangeStatus.data.currency_from]?.extra_id,
          exchangeStatus?.data?.extra_id_from,
          () => copyValue(exchangeStatus?.data?.extra_id_from, 'Extra ID copied'),
          t('convert.extraIdReminder')
        )}

      <Box>
        <Typography color="gray" variant="caption">
          {t('convert.depositTimeCaption')}
        </Typography>
        <Touchable onClick={() => window.open(SUPPORT_URL, '_blank')}>
          <Link color="yellow" variant="h6" underline="none">
            {t('convert.support')}
          </Link>
        </Touchable>
      </Box>
    </Stack>
  )
}

export default ConvertResult
